import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { TABLES } from "../../../Apis/Tables";
import { getFullName } from "../../../Apis/functions";
import TableFunction from "../../../components/table/TableFunction";
import { SEARCH_MODEL } from "../../../components/table/Model/TableModel";
import { VIEW_TYPE } from "../../../enums/ViewType";
import { SimpleSearchWithId } from "../../../reduxToolkit/Search/SearchApi";
import { getResidentVerificationStatus, RESIDENT_STATUS_VIEW_TYPE } from "../Model/Model";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Logic of the verified/unverified users modal: paginated search of the residents
 * of the building that share the identity validation status of the resume card
 * that opened it, rendered as the card grid of the modal.
 *
 * @param {Object} props
 * @param {string} props.buildingId - Building the residents belong to.
 * @param {Object} props.userClassificationSelected - Classification card that opened the modal,
 *   its id matches a RESIDENT_STATUS value (VERIFIED / UNVERIFIED).
 * @returns {Object} Localization function, list state and the search handlers.
 */
const VerifiedUnverifiedUsersModalApp = ({ buildingId, userClassificationSelected }) => {
    const dispatch = useDispatch();
    const option = TABLES.BUILDING_APARTMENT_RESIDENT_IDENTITY;
    const [searchElement, setSearchElement] = useState('');
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [modified, setModified] = useState(false);
    // Search key the list was loaded with, the debounce of CustomSearch also runs
    // on mount and that first page is already brought by TableFunction
    const requestedSearchElement = useRef('');
    // Building the last fetch ran with, the detail can still be loading on mount
    const requestedBuildingId = useRef(null);

    /**
     * Loads the page of residents the grid renders, they are searched through the
     * BUILDING_APARTMENT_RESIDENT_IDENTITY model of the simple search by id, which
     * takes the building as the userId of the body and the identity validation
     * status of the classification as its viewType.
     *
     * @param {number} page - Page requested by the pagination, zero based.
     * @param {Object} [options] - Extra values sent by TableFunction.
     * @param {number} [options.rowsPerPage] - Page size in use.
     * @param {string} [options.searchElement] - Search value the fetch runs with.
     */
    const getData = (page, options) => {
        if (!buildingId) return;

        const key = options?.searchElement ?? searchElement;
        requestedSearchElement.current = key;
        requestedBuildingId.current = buildingId;

        const body = SEARCH_MODEL.setObject({
            page,
            size: options?.rowsPerPage || rowsPerPage,
            model: option,
            key,
            userId: buildingId,
            viewType: RESIDENT_STATUS_VIEW_TYPE[userClassificationSelected?.id] ?? null
        });

        dispatch(SimpleSearchWithId({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success !== true) return;

                setResults({
                    ...data,
                    // The card paints one name and the apartment it belongs to,
                    // the service returns the name split in two fields
                    content: data?.content?.map((resident) => ({
                        ...resident,
                        fullName: getFullName(resident?.name, resident?.lastName),
                        apartment: resident?.apartment || '',
                        // The chip of the card is the verification status of the resident
                        status: getResidentVerificationStatus(resident?.identityValidationId)
                    })) || []
                });
            }
        );
    };

    // Use TableFunction to handle the grid logic (pagination and the fetch of the list)
    const { t, pagination, rowsPerPage } = TableFunction({
        data: results,
        labelRowsPerPage: 'users_per_page',
        hasContainer: true,
        fixedPagination: false,
        view: VIEW_TYPE.GRID.name,
        searchElement,
        getData,
        modified,
        setModified
    });

    // Loads the list once the building detail arrives, the mount fetch of
    // TableFunction is skipped while there is no building to search with
    useEffect(() => {
        if (!buildingId || requestedBuildingId.current === buildingId) return;

        setModified(true);
    }, [buildingId]);

    /**
     * Search of CustomSearch, it runs debounced on every change of the input and
     * brings the first page back with the key typed.
     *
     * @param {string} value - Value of the search input.
     */
    const handleSearch = (value) => {
        // The debounce fires with the value the list already has both on mount
        // and when the modal only changed page
        if (requestedSearchElement.current === value) return;

        setModified(true);
    };

    /**
     * Reset of CustomSearch, clears the input and brings the first page back
     * without the search key.
     */
    const handleResetSearch = () => {
        setSearchElement('');
        setModified(true);
    };

    // Return the necessary variables and functions for use in other parts of the app
    return {
        t, // Translation function
        searchElement,
        setSearchElement,
        results,
        handleSearch,
        handleResetSearch,
        pagination
    };
}

export default VerifiedUnverifiedUsersModalApp; // Export the component to be used in other parts of the app
